import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { NumberOnlyDirective } from '@app/core/directives/number-only-directive';
import { ILastActiveElementDetails } from '@campaign-form/form-fields/creators-and-ctas/cta-details.interface';
import { ECustomFieldType, ICustomField } from '@core/directives/custom-cta-base.directive';
import { IManualDeliverableStatusConfig } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextDeliverableComponent } from '../shared/text-deliverable/text-deliverable.component';
import { ITextDeliverableConfig } from '../shared/text-deliverable/text-deliverable.interface';

@Component({
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatErrorComponent,
    NumberOnlyDirective,
    ReactiveFormsModule,
    StatusBadgeComponent,
    TextDeliverableComponent,
    TranslateModule,
  ],
  selector: 'app-chatbot-multi',
  templateUrl: './chatbot-multi.component.html',
})
export class ChatbotMultiComponent {
  // ── Inputs forwarded to app-text-deliverable ──────────────────────────────
  public readonly form = input.required<FormGroup>();
  public readonly creatorId = input.required<string | TNil>();
  public readonly ctaTemplateId = input<string | undefined>(undefined);
  public readonly isCustomMode = input.required<boolean>();
  public readonly creators = input.required<ICreator[]>();
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly isParentDeleteDisabled = input<boolean>(false);
  public readonly lastActiveElementDetails = input<Map<string, ILastActiveElementDetails>>(new Map());
  public readonly isExpanded = input<boolean>(false);
  public readonly isViewMode = input<boolean>(false);
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly showRequiredBadge = input<boolean>(false);
  public readonly showStatusBadge = input<boolean>(true);
  public readonly manualDeliverableStatusConfig = input<IManualDeliverableStatusConfig>();
  public readonly analyticsBadgeLoading = input<boolean>(false);
  public readonly analyticsKeywordCounts = input<((keyword: string) => number | null) | null>(null);
  public readonly analyticsKeywordJump = input<((keyword: string) => void) | null>(null);
  public readonly analyticsKeywordJumpTargets = input<Record<string, string[]>>({});
  public readonly analyticsKeywordJumpTargetsReady = input<boolean>(true);

  // ── Chatbot-multi specific ────────────────────────────────────────────────
  public readonly statusMinInterval = input<EAnalyticsStatus | TNil>(undefined);
  public readonly statusMaxInterval = input<EAnalyticsStatus | TNil>(undefined);

  // ── Outputs (forwarded from inner text-deliverable) ───────────────────────
  public readonly delete = output<void>();
  public readonly parentCheckboxChange = output<boolean>();

  // ── Config for the inner text-deliverable ────────────────────────────────
  public readonly chatbotMultiTextConfig: Partial<ITextDeliverableConfig> = {
    addButtonTranslationKey: 'pages.campaignForm.ctaDialog.ctaDetails.deliverables.chatbotMultiDeliverable.addKeyword',
    arrayFieldName: 'keywords',
    contentFieldName: 'text',
    highlightNamespace: 'chatbotMultiContent',
    lastActiveCannotBeDeletedTranslationKey:
      'pages.campaignForm.ctaDialog.ctaDetails.deliverables.chatbotMultiDeliverable.lastActiveKeywordCannotBeDeleted',
    placeholderTranslationKey: 'pages.campaignForm.ctaDialog.ctaDetails.deliverables.chatbotMultiDeliverable.keyword',
    subtitleTranslationKey: 'pages.campaignForm.ctaDialog.ctaDetails.deliverables.chatbotMultiDeliverable.subtitle',
    type: EDeliverableType.chatbotMulti,
  };

  /** Registers interval fields for custom-values management. */
  public readonly intervalManagedFields: ICustomField[] = [
    { name: 'minInterval', type: ECustomFieldType.string },
    { name: 'maxInterval', type: ECustomFieldType.string },
  ];

  public readonly EDeliverableType = EDeliverableType;

  public get minInterval(): FormControl<string | null> {
    return this.form().get('minInterval') as FormControl<string | null>;
  }

  public get maxInterval(): FormControl<string | null> {
    return this.form().get('maxInterval') as FormControl<string | null>;
  }
}
